import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import TenantProfileFields, {
  MAX_OFFERING_SENTENCES,
  profilePayload,
  profileValues,
  sentenceCount,
  type TenantProfileValues,
} from "../../../components/tenant-onboarding/TenantProfileFields.tsx";
import DesignedWorkflows from "../../../components/tenant-onboarding/DesignedWorkflows.tsx";
import TenantResearchProgress, {
  useTenantProfileRun,
} from "../../../components/tenant-onboarding/TenantResearchProgress.tsx";
import { isJobActive } from "../../../utils/status.ts";

/** The tenant profile: domain, what it sells, ICP and buyer persona, and research from the domain. */
function IcpPage() {
  const { selectedTenantId } = useTenant();
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState("");
  const [values, setValues] = useState<TenantProfileValues>(profileValues(null));

  const tenant = useQuery({
    queryKey: keys.tenant.detail(selectedTenantId ?? ""),
    queryFn: () => api.getTenant(selectedTenantId!),
    enabled: !!selectedTenantId,
  });
  const run = useTenantProfileRun(selectedTenantId);
  const researching = isJobActive(run.data?.status);

  useEffect(() => {
    if (tenant.data) {
      setDomain(tenant.data.domain ?? "");
      setValues(profileValues(tenant.data));
    }
  }, [tenant.data]);

  // A run that finishes while the page is open: research fills empty fields
  // (show them), then designs workflows like a design run (list them below).
  const wasResearching = useRef(false);
  const [finishedHere, setFinishedHere] = useState(false);
  useEffect(() => {
    if (wasResearching.current && run.data?.status === "completed") {
      setFinishedHere(true);
      const output = run.data.output ?? {};
      const designed = !!output.workflows?.length;
      if (run.data.kind === "design" || designed || output.workflow_design_error) {
        queryClient.invalidateQueries({ queryKey: keys.workflow.all });
      }
      if (run.data.kind === "design") {
        toast.success("Workflows designed: they are paused until you switch them on");
      } else {
        queryClient.invalidateQueries({ queryKey: keys.tenant.detail(selectedTenantId!) });
        queryClient.invalidateQueries({ queryKey: keys.tenant.all });
        toast.success(
          designed
            ? "Research done: empty fields were filled in and workflows drafted (paused)"
            : "Research done: empty fields were filled in",
        );
      }
    }
    wasResearching.current = researching;
  }, [researching, run.data, queryClient, selectedTenantId]);

  const saveDomain = useMutation({
    mutationFn: () => api.updateTenant(selectedTenantId!, { domain: domain.trim() || null }),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.tenant.detail(selectedTenantId!), updated);
      toast.success("Domain saved");
    },
  });

  const research = useMutation({
    mutationFn: () => api.startTenantProfileResearch(selectedTenantId!),
    onSuccess: (newRun) => {
      setFinishedHere(false);
      queryClient.setQueryData(keys.tenant.profileRun(selectedTenantId!), newRun);
    },
  });

  const design = useMutation({
    mutationFn: () => api.startTenantWorkflowDesign(selectedTenantId!),
    onSuccess: (newRun) => {
      setFinishedHere(false);
      queryClient.setQueryData(keys.tenant.profileRun(selectedTenantId!), newRun);
    },
  });

  const save = useMutation({
    mutationFn: () => api.updateTenantIcp(selectedTenantId!, profilePayload(values)),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.tenant.detail(selectedTenantId!), updated);
      toast.success("Profile saved");
    },
  });

  if (tenant.isLoading) return <CenteredSpinner />;

  const savedDomain = tenant.data?.domain ?? "";
  const domainDirty = domain.trim() !== savedDomain;
  // The designer works from the profile as saved, so it shows only once all
  // three fields are saved, and needs the offering within its limit and no
  // edits waiting to be saved.
  const saved = profileValues(tenant.data);
  const profileDirty = (Object.keys(saved) as (keyof TenantProfileValues)[]).some(
    (key) => values[key].trim() !== saved[key].trim(),
  );
  const canDesign = !!(
    saved.offering_text.trim() &&
    saved.icp_text.trim() &&
    saved.buyer_persona_text.trim()
  );
  const designBlocker =
    sentenceCount(saved.offering_text) > MAX_OFFERING_SENTENCES
      ? `Keep "What does the company sell?" to ${MAX_OFFERING_SENTENCES} sentences.`
      : profileDirty
        ? "Save your changes first."
        : researching
          ? "Wait for the current run to finish."
          : null;

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", mb: 2 }}>
        <TextField
          label="Website domain"
          placeholder="acme.com"
          size="small"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="outlined"
          disabled={!domainDirty}
          loading={saveDomain.isPending}
          onClick={() => saveDomain.mutate()}
        >
          Save domain
        </Button>
        <Tooltip title="Fill empty profile fields from the website, then draft signal and prospecting workflows from the profile. Workflows are created paused.">
          <span>
            <Button
              variant="outlined"
              disabled={!savedDomain || domainDirty || researching}
              loading={research.isPending}
              onClick={() => research.mutate()}
            >
              Research from domain
            </Button>
          </span>
        </Tooltip>
        {canDesign && (
          <Tooltip
            title={
              designBlocker ??
              "Draft signal and prospecting workflows from this profile. They are created paused."
            }
          >
            <span>
              <Button
                variant="outlined"
                disabled={!!designBlocker}
                loading={design.isPending}
                onClick={() => design.mutate()}
              >
                Design workflows
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      {run.data && (researching || run.data.status === "failed") && (
        <Box sx={{ mb: 3, maxWidth: 640 }}>
          <TenantResearchProgress run={run.data} />
        </Box>
      )}
      {finishedHere && run.data?.status === "completed" && (
        <Box sx={{ mb: 3, maxWidth: 640 }}>
          <DesignedWorkflows run={run.data} />
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <TenantProfileFields
          values={values}
          onChange={setValues}
          disabled={researching}
          minRows={8}
        />
      </Box>
      <Button
        variant="contained"
        onClick={() => save.mutate()}
        loading={save.isPending}
        disabled={researching || sentenceCount(values.offering_text) > MAX_OFFERING_SENTENCES}
      >
        Save
      </Button>
    </>
  );
}

export default IcpPage;
